import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import katex from 'katex';

import { tightenDigitCommas } from './latex-digit-comma';
import { escapeUnescapedPercent } from './latex-percent';
import { MathFormulaView } from './math-formula-view';

export interface MathFormulaOptions {
  onEditRequest: (latex: string, update: (newLatex: string) => void) => void;
}

export function renderMathFormula(latex: string): { html: string; error: string | null } {
  if (!latex.trim()) return { html: '', error: null };
  try {
    /*
     * Two repairs, both at render time so that content stored before either existed is fixed
     * and the stored source stays what the teacher typed.
     *
     *  - a bare `%` is a KaTeX comment and silently eats the rest of the source (latex-percent)
     *  - a comma between two digits gets the space of a list, so `2,403,403` draws with gaps
     *    (latex-digit-comma)
     */
    const html = katex.renderToString(tightenDigitCommas(escapeUnescapedPercent(latex)), {
      throwOnError: true,
      displayMode: false,
    });
    return { html, error: null };
  } catch (err) {
    const raw = err instanceof Error ? err.message : 'Invalid formula';
    const first = raw.split('\n')[0] ?? raw;
    return { html: '', error: first.replace(/^KaTeX parse error:\s*/i, '') };
  }
}

export function processMathHTML(html: string): string {
  if (!html.includes('data-math-latex')) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll<HTMLElement>('[data-math-latex]').forEach((span) => {
    const latex = span.getAttribute('data-math-latex') ?? '';
    if (!latex) return;
    const { html: rendered } = renderMathFormula(latex);
    if (rendered) span.innerHTML = rendered;
  });
  return div.innerHTML;
}

export const MathFormula = Node.create<MathFormulaOptions>({
  name: 'mathFormula',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { onEditRequest: () => {} };
  },

  addAttributes() {
    return { latex: { default: '' } };
  },

  parseHTML() {
    return [
      { tag: 'span[data-math-latex]', getAttrs: (el) => ({ latex: (el as HTMLElement).getAttribute('data-math-latex') ?? '' }) },
      { tag: 'div[data-math-latex]',  getAttrs: (el) => ({ latex: (el as HTMLElement).getAttribute('data-math-latex') ?? '' }) },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        { 'data-math-latex': node.attrs.latex, contenteditable: 'false', class: 'math-formula' },
        HTMLAttributes,
      ),
      String(node.attrs.latex),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MathFormulaView);
  },
});
